import * as Sentry from '@sentry/react';

export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  beforeSend?: (event: any) => any;
}

class SentryService {
  private static instance: SentryService;
  private initialized = false;

  private constructor() {}

  public static getInstance(): SentryService {
    if (!SentryService.instance) {
      SentryService.instance = new SentryService();
    }
    return SentryService.instance;
  }

  /**
   * Initialize Sentry
   */
  public initialize(config?: Partial<SentryConfig>): void {
    if (this.initialized) {
      console.warn('Sentry already initialized');
      return;
    }

    const defaultConfig: SentryConfig = {
      dsn: import.meta.env.VITE_SENTRY_DSN || '',
      environment: import.meta.env.NODE_ENV || 'development',
      tracesSampleRate: import.meta.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      beforeSend: this.beforeSend
    };

    const finalConfig = { ...defaultConfig, ...config };

    if (!finalConfig.dsn) {
      // Silently skip initialization if DSN is not provided
      return;
    }

    try {
      Sentry.init({
        dsn: finalConfig.dsn,
        environment: finalConfig.environment,
        tracesSampleRate: finalConfig.tracesSampleRate,
        beforeSend: finalConfig.beforeSend,
        integrations: [
          // Basic integrations only
        ],
      });

      this.initialized = true;
      console.log('Sentry initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
    }
  }

  /**
   * Filter sensitive data before sending to Sentry
   */
  private beforeSend(event: any): any {
    // Remove sensitive data
    if (event.user) {
      delete event.user.email;
      delete event.user.phone;
    }

    // Remove API keys and secrets from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => {
        if (breadcrumb.data) {
          // Remove sensitive fields
          const sensitiveFields = ['apiKey', 'apiSecret', 'password', 'token'];
          sensitiveFields.forEach(field => {
            if (breadcrumb.data[field]) {
              breadcrumb.data[field] = '[REDACTED]';
            }
          });
        }
        return breadcrumb;
      });
    }

    // Remove sensitive data from extra context
    if (event.extra) {
      const sensitiveFields = ['apiKey', 'apiSecret', 'password', 'token'];
      sensitiveFields.forEach(field => {
        if (event.extra[field]) {
          event.extra[field] = '[REDACTED]';
        }
      });
    }

    return event;
  }

  /**
   * Set user context
   */
  public setUser(user: { id?: string; email?: string; level?: number }): void {
    if (!this.initialized) return;

    Sentry.setUser({
      id: user.id,
      email: user.email ? this.maskEmail(user.email) : undefined,
      level: user.level
    });
  }

  /**
   * Clear user context
   */
  public clearUser(): void {
    if (!this.initialized) return;

    Sentry.setUser(null);
  }

  /**
   * Set custom context
   */
  public setContext(key: string, context: any): void {
    if (!this.initialized) return;

    Sentry.setContext(key, context);
  }

  /**
   * Add breadcrumb
   */
  public addBreadcrumb(breadcrumb: {
    message: string;
    category?: string;
    level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
    data?: any;
  }): void {
    if (!this.initialized) return;

    Sentry.addBreadcrumb(breadcrumb);
  }

  /**
   * Capture exception
   */
  public captureException(error: Error, context?: any): void {
    if (!this.initialized) return;

    if (context) {
      Sentry.withScope((scope) => {
        Object.keys(context).forEach(key => {
          scope.setContext(key, context[key]);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  }

  /**
   * Capture message
   */
  public captureMessage(message: string, level: 'debug' | 'info' | 'warning' | 'error' | 'fatal' = 'info'): void {
    if (!this.initialized) return;

    Sentry.captureMessage(message, level);
  }

  /**
   * Start transaction (simplified)
   */
  public startTransaction(name: string, op: string): any {
    if (!this.initialized) return null;

    // Simple transaction tracking
    return {
      name,
      op,
      finish: () => {},
      setTag: () => {},
      setData: () => {}
    };
  }

  /**
   * Track performance (simplified)
   */
  public trackPerformance(_name: string, fn: () => Promise<any>): Promise<any> {
    if (!this.initialized) return fn();

    return fn();
  }

  /**
   * Mask email for privacy
   */
  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return email;
    
    const maskedLocal = local[0] + '*'.repeat(local.length - 2) + local[local.length - 1];
    return `${maskedLocal}@${domain}`;
  }

  /**
   * Check if Sentry is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get Sentry instance
   */
  public getSentry(): typeof Sentry {
    return Sentry;
  }
}

export const sentryService = SentryService.getInstance();